import { ComponentType, FC, ReactNode, useMemo } from 'react';
import { FeatureFlag } from '../../../gameConfig/sharedTypes';
import { useFeature, useSiteConfig } from '../../scripts/selectors/global';
import { AwcMidnightCup3Upsell } from './upsells/awcMidnightCup3';
import { ElekkGiveawayUpsell } from './upsells/elekkGiveaway';
import { MdiMidnightGroupsUpsell } from './upsells/mdiMidnightGroups';
import { MdiReplayRaceMnS1Upsell } from './upsells/mdiReplayRaceMnS1';
import { MidnightAddonsUpsell } from './upsells/midnightAddons';
import { MidnightAffixesUpsell } from './upsells/midnightAffixes';
import { MostOfRioMidnightUpsell } from './upsells/mostOfRioMidnight';

type TGameSite = 'wowretail' | 'wowclassic' | 'wowera';

type TUpsellConfig = {
  component: ComponentType<TUpsellProps> | null;
  startAt?: string;
  endAt?: string;
  weight: number;
  /** Restrict this upsell to specific game sites. Omit to show on all of them. */
  games?: TGameSite[];
};

const MIDNIGHT_TIME_TRIALS_ENDS_AT = '2026-04-21T01:00:00Z';
const ELEKK_GIVEAWAY_STARTS_AT = '2026-05-11T00:00:00Z';
const ELEKK_GIVEAWAY_ENDS_AT = '2026-05-23T00:00:00Z';
const AWC_MIDNIGHT_CUP_3_STARTS_AT = '2026-05-01T17:00:00Z';
const AWC_MIDNIGHT_CUP_3_ENDS_AT = '2026-05-04T03:00:00Z';
const MDI_MIDNIGHT_GROUP_A_STARTS_AT = '2026-05-07T17:00:00Z';
const MDI_MIDNIGHT_GROUP_A_ENDS_AT = '2026-05-11T02:00:00Z';
const MDI_MIDNIGHT_GROUP_B_STARTS_AT = '2026-05-14T17:00:00Z';
const MDI_MIDNIGHT_GROUP_B_ENDS_AT = '2026-05-18T02:00:00Z';
const MDI_MIDNIGHT_GROUP_C_STARTS_AT = '2026-05-21T17:00:00Z';
const MDI_MIDNIGHT_GROUP_C_ENDS_AT = '2026-05-25T02:00:00Z';
const MDI_REPLAY_RACE_MN_S1_STARTS_AT = '2026-05-26T20:00:00Z'; // 1 PM PDT (UTC-7)
const MDI_REPLAY_RACE_MN_S1_ENDS_AT = '2026-06-09T15:00:00Z'; // US weekly reset (Tue 8 AM PDT)

const ACTIVE_SHARED_UPSELLS: TUpsellConfig[] = [
  {
    component: MdiReplayRaceMnS1Upsell,
    weight: 1,
    startAt: MDI_REPLAY_RACE_MN_S1_STARTS_AT,
    endAt: MDI_REPLAY_RACE_MN_S1_ENDS_AT,
    games: ['wowretail']
  },
  {
    component: ElekkGiveawayUpsell,
    weight: 1,
    startAt: ELEKK_GIVEAWAY_STARTS_AT,
    endAt: ELEKK_GIVEAWAY_ENDS_AT
  },
  {
    component: AwcMidnightCup3Upsell,
    weight: 1,
    startAt: AWC_MIDNIGHT_CUP_3_STARTS_AT,
    endAt: AWC_MIDNIGHT_CUP_3_ENDS_AT
  },
  {
    component: MdiMidnightGroupsUpsell,
    weight: 1,
    startAt: MDI_MIDNIGHT_GROUP_A_STARTS_AT,
    endAt: MDI_MIDNIGHT_GROUP_A_ENDS_AT
  },
  {
    component: MdiMidnightGroupsUpsell,
    weight: 1,
    startAt: MDI_MIDNIGHT_GROUP_B_STARTS_AT,
    endAt: MDI_MIDNIGHT_GROUP_B_ENDS_AT
  },
  {
    component: MdiMidnightGroupsUpsell,
    weight: 1,
    startAt: MDI_MIDNIGHT_GROUP_C_STARTS_AT,
    endAt: MDI_MIDNIGHT_GROUP_C_ENDS_AT
  },
  {
    component: MidnightAddonsUpsell,
    weight: 1,
    startAt: MIDNIGHT_TIME_TRIALS_ENDS_AT,
    endAt: '2026-04-21T22:00:00Z'
  },
  {
    component: MostOfRioMidnightUpsell,
    weight: 3,
    startAt: MIDNIGHT_TIME_TRIALS_ENDS_AT,
    endAt: '2026-04-21T22:00:00Z'
  },
  {
    component: MidnightAffixesUpsell,
    weight: 1,
    startAt: MIDNIGHT_TIME_TRIALS_ENDS_AT,
    endAt: '2026-04-21T22:00:00Z'
  }
];

const UPSELLS: {
  withAds: TUpsellConfig[];
  noAds: TUpsellConfig[];
} = {
  withAds: [...ACTIVE_SHARED_UPSELLS, { component: null, weight: 1 }],
  noAds: [...ACTIVE_SHARED_UPSELLS, { component: null, weight: 2 }]
};

function isUpsellActive(upsell: TUpsellConfig, now: Date): boolean {
  if (upsell.games && !upsell.games.includes(RIO_SITE.game as TGameSite)) {
    return false;
  }

  if (upsell.startAt) {
    const startAt = new Date(upsell.startAt);
    if (now < startAt) {
      return false;
    }
  }

  if (upsell.endAt) {
    const endAt = new Date(upsell.endAt);
    if (now > endAt) {
      return false;
    }
  }

  return true;
}

type TUpsellProps = {
  seedOffset?: number;
  className?: string;
  fallback?: ReactNode;
};

export const Upsell: FC<TUpsellProps> = (props: TUpsellProps) => {
  const noAds = useFeature(FeatureFlag.NO_ADS);
  const siteConfig = useSiteConfig();

  const seed = siteConfig.seed + (props.seedOffset || 0);

  const selectedUpsell = useMemo(() => {
    const upsellList = noAds ? UPSELLS.noAds : UPSELLS.withAds;
    const now = new Date();

    // Filter to only active upsells based on date
    const activeUpsells = upsellList.filter((upsell) => isUpsellActive(upsell, now));

    if (activeUpsells.length === 0) {
      return null;
    }

    // Calculate total weight for weighted selection
    const totalWeight = activeUpsells.reduce((sum, upsell) => sum + upsell.weight, 0);

    // Guard against zero total weight (shouldn't happen, but prevents division by zero)
    if (totalWeight === 0) {
      return null;
    }

    // Use seed to select from weighted upsells
    // selectedIndex will be in range [0, totalWeight - 1]
    const selectedIndex = seed % totalWeight;

    let currentWeight = 0;
    let selectedUpsellConfig: TUpsellConfig | null = null;

    // Iterate through upsells, accumulating weight
    // When selectedIndex falls within an upsell's weight range, select it
    // Example: weights [2, 3], totalWeight = 5
    //   selectedIndex 0-1 → select upsell 1 (currentWeight becomes 2, 0-1 < 2)
    //   selectedIndex 2-4 → select upsell 2 (currentWeight becomes 5, 2-4 < 5)
    for (const upsell of activeUpsells) {
      currentWeight += upsell.weight;
      if (selectedIndex < currentWeight) {
        selectedUpsellConfig = upsell;
        break;
      }
    }

    // This should never be null due to the algorithm, but TypeScript needs the check
    return selectedUpsellConfig?.component || null;
  }, [noAds, seed]);

  if (!selectedUpsell) {
    return props.fallback ?? null;
  }

  const SelectedComponent = selectedUpsell;
  return <SelectedComponent {...props} />;
};
